/**
 * Shared kinematic-feel kernel: exponential smoothing/decay and release
 * momentum. This is deliberately NOT physics — no forces, no collisions,
 * no solver — just the two exponentials every "weighty" feel in the engine
 * is built from:
 *
 * - smoothing toward a target: blend = 1 − e^(−dt/τ)  (Billboard/Tether damping)
 * - decaying a velocity:       rate  = v₀ · e^(−t/τ)  (drag-release momentum)
 *
 * Momentum trajectories are evaluated in CLOSED FORM from total elapsed time
 * (`glideSweep`), never integrated per frame — that makes them frame-rate
 * independent to float precision, which is the cross-platform parity
 * contract. See docs-internal/MANIPULATION_MOMENTUM_SPEC.md.
 */

/** e^(−dt/τ). τ ≤ 0 → 0 (fully decayed / smoothing disabled). */
export function expDecay(dtSeconds: number, tauSeconds: number): number {
    if (tauSeconds <= 0) return 0;
    return Math.exp(-dtSeconds / tauSeconds);
}

/**
 * Frame blend for exponential smoothing toward a target: 1 − e^(−dt/τ).
 * τ ≤ 0 → 1 (instant). The formula Billboard and Tether damping use.
 */
export function smoothingBlend(dtSeconds: number, tauSeconds: number): number {
    return 1 - expDecay(dtSeconds, tauSeconds);
}

/**
 * Closed-form glide sweep: total distance/angle traveled after `t` seconds
 * of exponential velocity decay from `v0` with time constant `tau`:
 *
 *   sweep(t) = v₀ · τ · (1 − e^(−t/τ))     (converges to v₀ · τ)
 *
 * Evaluate ABSOLUTELY from elapsed time — a hitch simply jumps further along
 * the same curve; two machines at different frame rates land on identical
 * values.
 */
export function glideSweep(v0: number, tauSeconds: number, tSeconds: number): number {
    if (tauSeconds <= 0) return 0;
    return v0 * tauSeconds * (1 - Math.exp(-tSeconds / tauSeconds));
}

/** Instantaneous glide velocity at elapsed `t`: v₀ · e^(−t/τ). */
export function glideRate(v0: number, tauSeconds: number, tSeconds: number): number {
    return v0 * expDecay(tSeconds, tauSeconds);
}

/**
 * Trailing-window velocity estimator over timestamped samples of an
 * accumulating value (e.g. a drag's accumulated rotation). Rules, all
 * load-bearing for feel and specced for cross-platform parity:
 *
 * - Velocity = (newest − oldest) / span over the trailing `windowMs`.
 * - A release with the newest sample older than `staleMs` reads 0 — a finger
 *   held still then lifted must not fling.
 * - Fewer than 2 samples in the window, or a span under `minSpanMs`, reads 0
 *   — a single coalesced jump is not a measurable gesture.
 */
export class VelocityTracker {
    private _samples: Array<{ v: number; t: number }> = [];
    private readonly _windowMs: number;
    private readonly _staleMs: number;
    private readonly _minSpanMs: number;

    constructor(windowMs = 120, staleMs = 100, minSpanMs = 20) {
        this._windowMs = windowMs;
        this._staleMs = staleMs;
        this._minSpanMs = minSpanMs;
    }

    push(value: number, timeMs: number): void {
        this._samples.push({ v: value, t: timeMs });
        const cutoff = timeMs - this._windowMs;
        while (this._samples.length > 0 && this._samples[0].t < cutoff) {
            this._samples.shift();
        }
    }

    reset(): void {
        this._samples = [];
    }

    /** Velocity in value-units per SECOND as of `nowMs`; 0 when unmeasurable. */
    velocity(nowMs: number): number {
        const s = this._samples;
        if (s.length < 2) return 0;
        const newest = s[s.length - 1];
        if (nowMs - newest.t > this._staleMs) return 0;
        // Trim to the window relative to now (samples may have aged since push)
        let first = 0;
        const cutoff = nowMs - this._windowMs;
        while (first < s.length - 1 && s[first].t < cutoff) first++;
        const oldest = s[first];
        const spanMs = newest.t - oldest.t;
        if (spanMs < this._minSpanMs) return 0;
        return (newest.v - oldest.v) / (spanMs / 1000);
    }
}
